import { router, usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeImage } from "@/components/badges/badge-image"
import { PremiumBadge } from "@/components/badges/premium-badge"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { CELEBRATION_ATTR, useOverlayOpen } from "@/hooks/use-overlay-open"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { creatorBadgesPath, creatorProfileBadgesSeenPath } from "@/routes"
import type { PendingBadge } from "@/types/badges"

interface BadgeEarnModalSharedSlice {
  pending_badge?: PendingBadge | null
  // Index signature satisfies the `usePage<T extends PageProps>` constraint.
  [key: string]: unknown
}

/**
 * Soft radial halo behind the medallion. Purely decorative
 * (`aria-hidden`) — the textual "BADGE EARNED" eyebrow + badge-name
 * title carry the achievement, never the glow (UX Spec §6: colour is
 * never the sole signal). Rendered ONLY when motion is allowed; under
 * `prefers-reduced-motion` it is not in the DOM at all (UX Spec §4).
 */
function BadgeHalo() {
  return (
    <span
      aria-hidden="true"
      data-testid="badge-earn-halo"
      className={cn(
        "pointer-events-none absolute inset-0 -z-10 m-auto size-32 rounded-full",
        "bg-[radial-gradient(circle,rgba(251,191,36,0.45)_0%,rgba(251,191,36,0)_70%)]",
        "motion-safe:animate-rank-settle",
      )}
    />
  )
}

/**
 * OKS-402 — Badge-earned celebration modal.
 *
 * Mounted in `creator-shell.tsx` (next to `<RankUpModal>`) so every
 * creator page render gets the shared-prop pickup path. Subscribes to
 * `usePage().props.pending_badge` — the BE emits
 * `{ name, reason, xp_reward, icon, tier }` for the single
 * most-recently-earned un-celebrated badge on the current membership,
 * or `null`. The trigger is the next-render pull (same as rank-up);
 * real-time push is OKS-406.
 *
 * # Open / dismiss
 *
 * The modal opens when a non-null `pending_badge` arrives whose name
 * hasn't already been dismissed this session (`dismissedName` — the
 * name flipping signals a NEW badge). EVERY close path — primary "See
 * all badges", ghost "Maybe later", the X, backdrop, Esc — routes
 * through `dismiss()`, which optimistically closes the modal and fires
 * `PATCH /profile/badges/seen` (`Profile::BadgeSeenService`, atomic
 * two-axis write). The close is optimistic: if the PATCH fails
 * (offline / 5xx) the badge stays un-celebrated server-side and the
 * modal simply reappears on the next render — idempotent, no blocking
 * error UI (UX Spec §3 Error).
 *
 * # Reduced motion
 *
 * Under `prefers-reduced-motion` the halo + entrance pop are suppressed
 * (halo not rendered; pop collapses to the Dialog's default fade).
 * Content + dismissal behaviour are identical (UX Spec §4 / §6).
 *
 * # No queue
 *
 * When several badges are eligible the BE returns only the most recent
 * in `pending_badge`, and dismissal marks ALL eligible badges
 * celebrated, so this component renders exactly ONE modal (UX Spec §1
 * edge).
 */
export function BadgeEarnModal() {
  const { t } = useTranslation()
  const pending = usePage<BadgeEarnModalSharedSlice>().props.pending_badge
  const reducedMotion = usePrefersReducedMotion()

  // `open` is DERIVED (no setState-in-effect): the modal is open
  // whenever a non-null `pending_badge` is present whose name we haven't
  // dismissed. A NEW badge (different name) re-opens automatically; the
  // same name after dismissal stays closed.
  const [dismissedName, setDismissedName] = useState<string | null>(null)

  // OKS-966 — defer while ANY other modal overlay is open. A creator
  // mid-task (TikTok picker, submission confirm, a sheet) has a stronger
  // claim to their own clicks than a celebration does, and a modal mounted
  // over theirs intercepts them — the same defect
  // `pending_badge_props` already resolves against `pending_rank_up`
  // server-side, which cannot see client overlays.
  //
  // This is a DEFERRAL, not a dismissal: `open` false renders nothing and
  // calls no `dismiss()`, so the badge stays un-celebrated server-side and
  // this expression re-derives to true the moment the overlay closes. Same
  // posture as the rank-up path.
  const overlayOpen = useOverlayOpen()
  const open = pending != null && pending.name !== dismissedName && !overlayOpen

  // Marks the badge(s) seen. `onFinish` (optional) is threaded straight
  // into the mark-seen visit's options so a caller can CHAIN a follow-up
  // navigation onto it (see `handleSeeAll`). It is deliberately
  // `onFinish` — not `onSuccess` — so the callback runs on success,
  // error, AND cancel: a failed/offline mark-seen must never strand the
  // caller (OKS-642). The dismiss-in-place callers (X / backdrop / Esc /
  // "Maybe later") pass no callback and are unaffected.
  const dismiss = (options: { onFinish?: () => void } = {}) => {
    if (pending) setDismissedName(pending.name)
    router.patch(
      creatorProfileBadgesSeenPath(),
      {},
      {
        preserveScroll: true,
        preserveState: true,
        // No-op `only` keeps the dismissal response minimal; the
        // `pending_badge` prop clears on the next full visit.
        only: [],
        onFinish: options.onFinish,
      },
    )
  }

  const handleOpenChange = (next: boolean) => {
    // Radix fires this for X / backdrop / Esc. Closing always dismisses.
    if (!next) dismiss()
  }

  // OKS-642 — the primary CTA must reliably land the creator on the
  // Badges page. The old shape fired the mark-seen PATCH and
  // `router.visit(badges)` CONCURRENTLY on the single Inertia router,
  // which cancels one against the other → the navigation was
  // intermittently dropped and the modal stayed stuck. Fix: CHAIN the
  // navigation onto the mark-seen visit's `onFinish`, so exactly one
  // visit is ever in flight and the navigation always runs once the
  // PATCH settles (success, error, or cancel — navigation is never
  // blocked by a mark-seen failure). `setDismissedName` still closes the
  // modal optimistically first, so it never visibly lingers.
  const handleSeeAll = () => {
    dismiss({ onFinish: () => router.visit(creatorBadgesPath()) })
  }

  // Empty / not-triggered state: nothing rendered (no overlay, no DOM).
  // `pending` stays non-null through the close-out animation (the
  // dismissal PATCH uses `only: []`, so props don't refresh until the
  // next navigation), so the celebration content remains stable as it
  // animates out.
  if (!pending) return null

  // With a reason we render a `DialogDescription` and let Radix wire
  // `aria-describedby` to it. Without one we opt out explicitly so Radix
  // doesn't warn — the badge-name title alone conveys it (UX Spec §6).
  const describedByOptOut = pending.reason
    ? {}
    : { "aria-describedby": undefined }
  const showXpChip = pending.xp_reward != null && pending.xp_reward > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="badge-earn-modal"
        // ⚠️ Load-bearing, not decoration: `useOverlayOpen` ignores elements
        // carrying this marker. Without it this dialog detects ITSELF —
        // closes, stops being detected, re-opens — forever. Removing it does
        // not restore the old behaviour, it produces the loop.
        {...{ [CELEBRATION_ATTR]: "" }}
        {...describedByOptOut}
        className="overflow-hidden sm:max-w-sm"
      >
        <div
          className={cn(
            "flex flex-col items-center gap-3 py-4 text-center",
            // OKS-562 §14 — the purpose-built `badge-earn` beat (was the
            // borrowed rank-up `rank-pop`). The badge pops in once.
            MOTION_BEATS["badge-earn"],
          )}
        >
          <span className="relative inline-flex items-center justify-center">
            {!reducedMotion && <BadgeHalo />}
            {/* OKS-538/573 — custom badges always use the frame-less
                image (tinted default when null); a milestone uses the
                image only when a brand override is present, else keeps
                the Lucide medallion. */}
            {pending.kind === "custom" || pending.image_url != null ? (
              <BadgeImage
                imageUrl={pending.image_url}
                accentColor={pending.accent_color}
                name={pending.name}
                state="earned"
                size="modal"
              />
            ) : (
              <PremiumBadge
                tier={pending.tier}
                icon={pending.icon}
                state="earned"
                size="hero"
              />
            )}
          </span>

          <span
            aria-hidden="true"
            className="text-primary text-xs font-semibold tracking-[0.25em] uppercase"
          >
            {t("badges.earn_modal.eyebrow")}
          </span>

          <DialogTitle
            aria-label={t("badges.earn_modal.title_aria", {
              name: pending.name,
            })}
            className="text-2xl leading-tight font-bold text-balance"
          >
            {pending.name}
          </DialogTitle>

          {pending.reason && (
            <DialogDescription className="text-balance">
              {pending.reason}
            </DialogDescription>
          )}

          {showXpChip && (
            <Badge
              data-testid="badge-earn-xp-chip"
              className="border-transparent bg-amber-100 text-amber-900"
            >
              {t("badges.earn_modal.xp_chip", { count: pending.xp_reward })}
            </Badge>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:justify-center">
          <Button
            data-testid="badge-earn-modal-see-all"
            className="w-full"
            onClick={handleSeeAll}
          >
            {t("badges.earn_modal.cta_see_all")}
          </Button>
          <Button
            variant="ghost"
            data-testid="badge-earn-modal-later"
            className="w-full"
            // Wrap so the click MouseEvent isn't passed as `dismiss`'s
            // options arg (dismiss-in-place: no chained navigation).
            onClick={() => dismiss()}
          >
            {t("badges.earn_modal.cta_later")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
